import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import html2canvas from "html2canvas";

import {
  getAnalysisJob,
  getAnalysisStatus,
  getAnalysis,
  getType,
  getLifetimeWordReport,
  getLastFileDetails,
} from "../../../network/cipAPI";
import AdvanceSettings from "../../analyseData/advancedSettings";
import {
  storeAnalysisData,
  storeTaskId,
} from "../../../Store/TemplateStore/template.action";

import ReplacementResultChart from "../charts";
import PageLoading from "../../../components/PageLoading";
import { OverlayLoader } from "../../../components/PageLoading";
import AgTable from "../../../components/AgTable";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../../components/Notifications";
import DupontButton from "../../../components/DupontButton";
import exportAllReplacementTables from "../../../XLSDownload/ExportAllTables/raTableExport";
import { WPRangedInput } from "../../../packages/WPInput/WPRangedInput";
import { AccordionDownIcon } from "../../../packages/SVGs";
import WPLoader from "../../../packages/WPLoader/WPLoader.jsx";
import { WPDropDownMenu } from "../../../packages/WPDropDownMenu/WPDropDownMenu.jsx";
import {
  FORECAST_WINDOW,
  PLOTLY_CLASS_NAME,
} from "../../../constants/global.constants";
import { createDownloadMenuItems } from "../../../common/configs/downloadMenuConfig";

import ExportAllData from "./exportAllData";

import { RELACEMENT_ANALYSIS_COL_DEF } from "./replacementAnalysisTable.config";

import "./index.scss";

const ReplacementResult = ({ setDisableNextButton, detailsResult }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const intervalIdRef = useRef(null);
  const chartRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [showOverlayLoader, setShowOverlayLoader] = useState(false);
  const [processedPercentage, setprocessedPercentage] = useState("1%");
  const [taskId, setTaskId] = useState(useSelector((state) => state.taskId));
  const [analysisResult, setAnalysisResult] = useState(null);
  const [rowData, setRowData] = useState(null);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [translatedRowData, setTranslatedRowData] = useState(null);
  const [isWordDownloading, setIsWordDownloading] = useState(false);
  const [plotData, setPlotData] = useState(null);
  const [errorMsgFailedPrediction, setErrorMsgFailedPrediction] =
    useState(null);
  const plantLocation = useSelector((state) => state.plantLocation);
  const plantName = useSelector((state) => state.plantName);
  const selectedTrain = useSelector((state) => state.selTrain);
  const [exportAllSheetName, setExportAllSheetName] = useState(
    `${plantLocation.trim()}_${plantName.trim()}_${moment().format(
      "MM/DD/YYYY"
    )}_${t("replacementAnalysisStrings.replacementAdvisorReport")}`
  );
  const allData = ExportAllData();
  const [exportAllData, setExportAllData] = useState({
    ...allData,
    replacementAnalysisData: rowData,
    replacementPredictionData: plotData,
  });
  const [forecastDays, setForecastDays] = useState(
    useSelector((state) => state.forecast_window)
  );
  const [lastAnalyzedForecast, setLastAnalyzedForecast] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const colDef = RELACEMENT_ANALYSIS_COL_DEF(t, translatedRowData);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const handleSettingsOpen = () => {
    setIsSettingsDialogOpen(true);
  };

  const handleSettingsClose = () => {
    setIsSettingsDialogOpen(false);
  };

  const handleAdvanceSettingsSave = () => {
    // Trigger the analysis API call again when advance settings are saved
    onReplacementResultClicked();
  };
  useEffect(() => {
    setExportAllData({
      ...exportAllData,
      replacementAnalysisData: rowData,
      replacementPredictionData: plotData,
    });
  }, [rowData, plotData]);

  const handleForecastChange = (e) => {
    const value = parseInt(e?.target?.value ?? e) || null;
    setErrorMsg("");

    if (value && value >= FORECAST_WINDOW.MIN && value <= FORECAST_WINDOW.MAX) {
      setForecastDays(value);
      setExportAllData((prevState) => ({
        ...prevState,
        others: [
          {
            ...prevState.others[0],
            forecast_window: value,
          },
        ],
      }));
    } else {
      setForecastDays(value);
      setErrorMsg(t("replacementAnalysisStrings.forecastWindowError"));
    }
  };
  const checkStatus = async (task_id) => {
    try {
      let id = task_id || taskId;
      let analysisStatus;
      if (id === "" || id === null || id === undefined) {
        analysisStatus = { data: { status: "pending" } };
      } else {
        analysisStatus = await getAnalysisStatus(id);
      }

      const status = analysisStatus?.data?.status?.toLowerCase();
      try {
        if (status === "completed") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          const analysisResult = await getAnalysis(selectedTrain, forecastDays);

          setShowOverlayLoader(false);
          setIsLoading(false);
          setDisableNextButton(false);
          if (analysisResult?.data["modeling"]) {
            try {
              const parsedData = JSON.parse(analysisResult?.data["modeling"]);
              dispatch(storeAnalysisData(parsedData));
              setAnalysisResult(parsedData);
              setRowData(parsedData.replacement_results);
              setPlotData(parsedData.plot_results);

              dispatch(storeTaskId(""));
              setTaskId("");
            } catch (error) {
              createNotification(
                ERROR_NOTIFICATION,
                t("notificationStrings.somethingWrong"),
                ""
              );
            } finally {
              setDisableNextButton(false);
              setIsLoading(false);
              setLastAnalyzedForecast(forecastDays);
            }
          }
          //  else {
          //   setIsLoading(false);
          // }
        } else if (status === "running") {
          setprocessedPercentage(analysisStatus?.data.processed);
          setIsLoading(true);
          setDisableNextButton(true);
          setShowOverlayLoader(false);
        } else if (status === "failed") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          setIsLoading(false);
          setDisableNextButton(false);
          setShowOverlayLoader(false);
          createNotification(
            ERROR_NOTIFICATION,
            t("predictionStrings.failedPrediction"),
            ""
          );
          setErrorMsgFailedPrediction(t("predictionStrings.errorPrediction"));
          dispatch(storeTaskId(""));
          setTaskId("");
          setRowData(null);
          setPlotData(null);
          dispatch(storeAnalysisData(null));
        } else if (status === "pending") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          setIsLoading(false);
          setShowOverlayLoader(false);
          setDisableNextButton(false);
        }
      } catch (error) {
        setShowOverlayLoader(false);
        setIsLoading(false);
        setDisableNextButton(false);
        console.error("Error fetching data:", error);
      }
    } catch (error) {
      if (error.code === "ERR_NETWORK" || error.response.status === 502) {
        console.log("Error is " + error);
      } else {
        console.error("Error fetching data:", error);
        setErrorMsgFailedPrediction(t("predictionStrings.errorPrediction"));
        setIsLoading(false);
        setDisableNextButton(false);
        setShowOverlayLoader(false);
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        dispatch(storeTaskId(""));
        setTaskId("");
      }
    }
  };

  const onReplacementResultClicked = async () => {
    setIsLoading(true);
    setDisableNextButton(true);
    setprocessedPercentage("1%");
    let task_id = taskId;

    if (!taskId) {
      setErrorMsgFailedPrediction(null);
      try {
        const convertedType = await getType();
        setTranslatedRowData(convertedType.data);
        let response = await getAnalysisJob(forecastDays, selectedTrain);
        task_id = response?.data?.task_id;
        dispatch(storeTaskId(task_id));
        setTaskId(task_id);

        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
        }
        intervalIdRef.current = setInterval(function () {
          checkStatus(task_id);
        }, 15000);
      } catch (error) {
        setIsLoading(false);
        setDisableNextButton(false);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          ""
        );
      }
    }
  };

  useEffect(() => {
    const fetchPreviousData = async () => {
      try {
        setIsLoading(true);
        setDisableNextButton(true);

        // Fetch last file details to get previous_ra_prediction
        const fileDetails = await getLastFileDetails(selectedTrain);
        const { previous_ra_prediction } = fileDetails.data;
        if (previous_ra_prediction) {
          const { modeling, forecast_window } = previous_ra_prediction;

          if (modeling) {
            const parsedData = JSON.parse(modeling);
            dispatch(storeAnalysisData(parsedData));
            setAnalysisResult(parsedData);
            setRowData(parsedData.replacement_results);
            setPlotData(parsedData.plot_results);

            if (forecast_window) {
              setForecastDays(Math.ceil(forecast_window));
              setLastAnalyzedForecast(Math.ceil(forecast_window));
            }
          }
        } else {
          dispatch(storeAnalysisData(null));
          setAnalysisResult(null);
          setRowData(null);
          setPlotData(null);
        }
      } catch (error) {
        console.error("Error fetching previous analysis data:", error);
      } finally {
        setIsLoading(false);
        setDisableNextButton(false);
      }
    };

    // Only fetch data when detailsResult becomes true (after PUT API completes)
    if (detailsResult) {
      fetchPreviousData();
    }

    // Cleanup function when component unmounts
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [detailsResult]);

  const handleExcelExport = () => {
    exportAllReplacementTables(
      exportAllSheetName,
      exportAllData,
      translatedRowData,
      t
    );
  };

  const handleWordDownload = async () => {
    try {
      setIsWordDownloading(true);
      await new Promise((resolve) => setTimeout(resolve, 200));
      // Get all Plotly chart divs from the chart container
      const plotlyDivs =
        chartRef.current?.querySelectorAll(PLOTLY_CLASS_NAME.CLASS_NAME) || [];

      // Convert all charts to base64 in parallel
      const chartConversion = Array.from(plotlyDivs).map(
        async (plotlyDiv, chartIndex) => {
          try {
            const canvas = await html2canvas(plotlyDiv);
            return canvas
              .toDataURL("image/png")
              .replace(/^data:image\/png;base64,/, "");
          } catch (error) {
            console.error(
              `Error converting chart ${chartIndex + 1} to base64:`,
              error
            );
            return null;
          }
        }
      );

      const charts = (await Promise.all(chartConversion)).filter(Boolean);
      const payload = {
        charts: charts,
        replacement_results: rowData || [],
        forecast_window: forecastDays,
      };

      // Call API
      const response = await getLifetimeWordReport(selectedTrain, payload);

      if (response && response.status === 200) {
        const blob = response.data;
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${exportAllSheetName}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          window.URL.revokeObjectURL(link.href);
        }, 100);
      }
    } catch (error) {
      console.error("Word download error:", error);
      createNotification(
        ERROR_NOTIFICATION,
        t("predictionStrings.wordReportDownloadFailed"),
        ""
      );
    } finally {
      setIsWordDownloading(false);
    }
  };

  const downloadMenuItems = createDownloadMenuItems(
    t,
    rowData !== null,
    isLoading,
    handleExcelExport,
    handleWordDownload
  );

  return (
    <div className="ag-theme-alpine container-padding-ra">
      {showOverlayLoader && <OverlayLoader customHeight={"350px"} />}
      <div className="flex-center-center">
        <div className="fitContent">
          <WPRangedInput
            value={forecastDays}
            label={t("replacementAnalysisStrings.forecastWindow")}
            onChange={handleForecastChange}
            ranges={{
              hard: { min: FORECAST_WINDOW.MIN, max: FORECAST_WINDOW.MAX },
            }}
            integer
            disabled={isLoading}
          />
        </div>
        <div>
          <DupontButton
            onClick={onReplacementResultClicked}
            customClass="margin-left-10 "
            disabled={
              !forecastDays ||
              isLoading ||
              errorMsg ||
              forecastDays === lastAnalyzedForecast
            }
          >
            {t("replacementAnalysisStrings.runReplacementAnalysis")}
          </DupontButton>
        </div>
        <div className={`text-right margin-left`}>
          <WPDropDownMenu
            toggle={
              <div className="predict-download-toggle">
                {t("predictionStrings.downloadReport")}
                <AccordionDownIcon />
              </div>
            }
            disabled={rowData === null || isLoading}
            customClass="predict-dropdown-tertiary"
            menuItems={downloadMenuItems}
          />
        </div>
      </div>
      {isWordDownloading && (
        <WPLoader loadingMsg={t("predictionStrings.generatingWordReport")} />
      )}
      {isLoading ? (
        <PageLoading
          customHeight={150}
          iconHeight={"height--100"}
          message={t("ValueStrings.loaderMessage")}
          percentage={processedPercentage}
        ></PageLoading>
      ) : (
        <div>
          {errorMsgFailedPrediction && (
            <div className="errorMsg">{errorMsgFailedPrediction}</div>
          )}

          {rowData && (
            <AgTable
              rowData={rowData}
              columnDefs={colDef}
              domLayout="autoHeight"
              name={`${t("excelSheetNames.replacementAnalysis")}.csv`}
            />
          )}
          {plotData && (
            <div className="margin-top-20" ref={chartRef}>
              <span className="descriptionText">
                {t("replacementAnalysisStrings.chartDescription")}
              </span>
              <ReplacementResultChart plotData={plotData} />
            </div>
          )}

          {rowData && (
            <div className="RO-disclaimer">
              {t("disclaimer", { currentYear })}
            </div>
          )}
        </div>
      )}
      <DupontButton
        customClass="fixed-settings-btn"
        onClick={handleSettingsOpen}
        disabled={isLoading}
      >
        {t("analyseData.settings")}
      </DupontButton>
      {isSettingsDialogOpen && (
        <AdvanceSettings
          isSettingsDialogOpen={isSettingsDialogOpen}
          handleSettingsDialogClose={handleSettingsClose}
          selectedTrain={selectedTrain}
          cipPredictionAndRecommendation
          lifetime
          onSave={handleAdvanceSettingsSave}
        />
      )}
    </div>
  );
};

export default ReplacementResult;
